import { useState } from "react";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AuthShell } from "@/components/AuthShell";
import { FormError } from "@/components/FormError";
import { authApi } from "@/api/endpoints/auth";
import { meApi } from "@/api/endpoints/me";
import { authStorage } from "@/lib/auth-storage";
import { ApiError } from "@/api/client";
import { translateError } from "@/i18n/errors";

const schema = z.object({
  email: z.string().email("Enter a valid email."),
  password: z.string().min(1, "Password is required."),
});
type FormValues = z.infer<typeof schema>;

export function LoginPage(): JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const search = useSearch({ strict: false }) as { redirect?: string };
  const [serverError, setServerError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>();

  const loginMutation = useMutation({
    mutationFn: async (values: FormValues) => {
      const parsed = schema.parse(values);
      const res = await authApi.login(parsed);
      authStorage.set(res.access_token);
      const me = await meApi.get();
      return me;
    },
    onSuccess: async (me) => {
      await queryClient.invalidateQueries();
      if (me.memberships.length === 0) {
        navigate({ to: "/onboarding/first-tenant" });
      } else {
        navigate({ to: search.redirect ?? "/dashboard" });
      }
    },
    onError: (err) => {
      if (err instanceof ApiError) setServerError(translateError(err.code, err.message));
      else setServerError(translateError("COMMON_UNKNOWN"));
    },
  });

  const onSubmit = handleSubmit((values) => {
    setServerError(null);
    loginMutation.mutate(values);
  });

  return (
    <AuthShell sideTitle="Sign in to your workspace.">
      <div>
        <h1>Sign in</h1>
        <p>Welcome back. Enter your email and password to continue.</p>
      </div>
      <form onSubmit={onSubmit} noValidate>
        <div className="field-group">
          <label htmlFor="email">Email</label>
          <input
            id="email"
            type="email"
            autoComplete="email"
            className="field-input"
            data-testid="login-email"
            {...register("email", { required: "Email is required." })}
          />
          <FormError message={errors.email?.message} />
        </div>
        <div className="field-group">
          <label htmlFor="password">Password</label>
          <input
            id="password"
            type="password"
            autoComplete="current-password"
            className="field-input"
            data-testid="login-password"
            {...register("password", { required: "Password is required." })}
          />
          <FormError message={errors.password?.message} />
        </div>
        <FormError message={serverError} />
        <button
          type="submit"
          className="btn btn-primary btn-lg btn-block"
          disabled={isSubmitting || loginMutation.isPending}
          data-testid="login-submit"
        >
          {loginMutation.isPending ? "Signing in…" : "Sign in"}
        </button>
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12 }}>
          <Link to="/forgot-password" style={{ color: "var(--accent)" }}>
            Forgot password?
          </Link>
          <Link to="/register" style={{ color: "var(--accent)" }}>
            Create account
          </Link>
        </div>
      </form>
    </AuthShell>
  );
}
